import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import EmployeePageShell from "./EmployeePageShell";
import useFetch from "../../useFetch";
import { apiRequest } from "../../utils/api";

const formatDate = (date) => {
  if (!date) return "Open until filled";

  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatJobMeta = (job) => {
  const parts = [
    job.company_name,
    job.location,
    job.job_type,
    job.workplace_type,
    job.application_deadline ? "Deadline: " + formatDate(job.application_deadline) : null,
  ].filter(Boolean);

  return parts.join(" - ");
};

const getStatusClass = (status) => {
  if (status === "closed") return "danger";
  if (status === "declined") return "danger";
  if (status === "expired") return "danger";
  if (status === "paused") return "warning";
  if (status === "pending_verification") return "warning";
  if (status === "pending_admin_review") return "warning";

  return "";
};

const statusLabels = {
  pending_admin_review: "Pending Review",
  pending_verification: "Pending Verification",
};

const ConfirmationPopup = ({ action, isLoading, error, onConfirm, onCancel }) => {
  if (!action) return null;

  return (
    <div className="employee-confirm-backdrop">
      <div className="employee-confirm-box">
        <h4>{action.title}</h4>
        <p>{error ?? action.message}</p>
        <div className="employee-confirm-actions">
          <button type="button" className="theme-btn btn-style-three" onClick={onCancel} disabled={isLoading}>
            Cancel
          </button>
          <button type="button" className="theme-btn btn-style-one" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? "Loading..." : action.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const EmployeeManageJobs = () => {
  const [apiUrl, setApiUrl] = useState(process.env.REACT_APP_BASE_API_URL + "/jobs?manage=1&per_page=50");
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState([]);
  const [action, setAction] = useState(null);
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [success, setSuccess] = useState(false);

  const { response, isLoading, error: fetchError } = useFetch(apiUrl);

  useEffect(() => {
    setItems(response?.jobs?.data ?? []);
    setMeta(response?.jobs?.meta ?? []);
  }, [response]);

  const refreshJobs = () => {
    setApiUrl(process.env.REACT_APP_BASE_API_URL + "/jobs?manage=1&per_page=50&_=" + Date.now());
  };

  const openAction = (type, job) => {
    setActionError(null);
    setSuccess(false);

    if (type === "pause") {
      setAction({
        type,
        job,
        title: "Pause Job?",
        message: "This job will be hidden from public listings until you continue it.",
        confirmText: "Pause Job",
      });
    }
    else if (type === "resume") {
      setAction({
        type,
        job,
        title: "Continue Job?",
        message: "This job will become active and visible in public listings again.",
        confirmText: "Continue Job",
      });
    }
    else if (type === "delete") {
      setAction({
        type,
        job,
        title: "Remove Job?",
        message: "This job will be removed from your manage jobs list.",
        confirmText: "Remove Job",
      });
    }
  };

  const handleConfirmAction = () => {
    if (!action) return;

    setIsLoadingAction(true);
    setActionError(null);

    const endpoint =
      action.type === "pause" ? `/jobs/${action.job.id}/pause` :
      action.type === "resume" ? `/jobs/${action.job.id}/resume` :
      `/jobs/${action.job.id}`;

    apiRequest(endpoint, {
      method: action.type === "delete" ? "DELETE" : "POST",
    })
      .then(data => {
        if (data?.status === "success") {
          setSuccess(data.message);
          setAction(null);
          refreshJobs();
        }
        else if (data?.status === "error" && data?.message) {
          setActionError(data.message);
        }
        else {
          setActionError("There was some error while updating job, please try again.");
        }

        setIsLoadingAction(false);
      })
      .catch(error => {
        setActionError(error.message);
        setIsLoadingAction(false);
      });
  };

  return (
    <EmployeePageShell
      active="manage-jobs"
      title="Manage Jobs"
      text="Review active, expiring, and expired listings."
    >
      <ConfirmationPopup
        action={action}
        isLoading={isLoadingAction}
        error={actionError}
        onConfirm={handleConfirmAction}
        onCancel={() => setAction(null)}
      />

      <div className="ls-widget">
        <div className="widget-title">
          <h4>Your Job Posts</h4>
          <div className="employee-widget-actions">
            <Link to="/employee-plans" className="theme-btn btn-style-three">Buy Credits</Link>
            <Link to="/employee-post-job" className="theme-btn btn-style-one">Post New Job</Link>
          </div>
        </div>
        <div className="widget-content">
          {success && (
            <div className="alert alert-success">
              <strong>{success}</strong>
            </div>
          )}

          {fetchError && (
            <div className="alert alert-danger">
              <strong>{fetchError}</strong>
            </div>
          )}

          {isLoading && <h5 className="text-center my-3">Loading Data</h5>}

          {!fetchError && !isLoading && items.length === 0 && (
            <div className="employee-empty-state">No jobs found. Post your first job to start receiving applicants.</div>
          )}

          <div className="employee-table-list">
            {!fetchError && !isLoading && items.map((job) => (
              <div className="employee-table-row" key={job.id}>
                <div>
                  <strong>{job.title}</strong>
                  <span>{formatJobMeta(job)}</span>
                </div>
                <div className="employee-row-actions">
                  <span className={getStatusClass(job.effective_status ?? job.status)}>{statusLabels[job.effective_status ?? job.status] ?? job.effective_status ?? job.status}</span>
                  <Link to={`/employee-edit-job/${job.id}`} className="theme-btn btn-style-three">
                    Edit
                  </Link>
                  {["pending_verification", "pending_admin_review", "declined"].includes(job.status) || (job.effective_status ?? job.status) === "expired" ? null : job.status === "paused" ? (
                    <button type="button" onClick={() => openAction("resume", job)} className="theme-btn btn-style-three">
                      Continue
                    </button>
                  ) : (
                    <button type="button" onClick={() => openAction("pause", job)} className="theme-btn btn-style-three">
                      Pause
                    </button>
                  )}
                  <Link to={`/employee-applicants/${job.id}`} className="theme-btn btn-style-three">
                    View Applicants
                  </Link>
                  <button type="button" onClick={() => openAction("delete", job)} className="theme-btn btn-style-three employee-danger-btn">
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          {!fetchError && !isLoading && meta?.total > 0 && (
            <i className="d-block text-center">({meta.total} records found)</i>
          )}
        </div>
      </div>
    </EmployeePageShell>
  );
};

export default EmployeeManageJobs;
